/**
 * CR500 Phase 2B: Side Panel Presentation Mode
 * 
 * Renders the deal card with a side drawer for details.
 * Scaffolded - full implementation renders same blocks as flip.
 */

import React from "react";
import { X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { Deal } from "@/lib/types/deals";
import type { ResolvedDealCardSpecV2 } from "@/lib/deal-cards/specV2/types";
import type { 
  DetailsEntryConfig, 
  SidePanelPresentationConfig,
} from "@/lib/layout-studio/detailsPresentationTypes";
import { DEFAULT_DETAILS_ENTRY, DEFAULT_SIDE_PANEL_CONFIG } from "@/lib/layout-studio/detailsPresentationTypes";
import { getDealCardTitle, getDealCardCondition } from "@/lib/deals/dealCardDisplay";
import { DetailsBlocksRenderer } from "./DetailsBlocksRenderer";
import { cn } from "@/lib/utils";

// ================================================================
// TYPES
// ================================================================

export interface SidePanelPresentationProps {
  /** The deal being displayed */
  deal: Deal & { id?: string };
  /** The resolved v2 spec */
  spec: ResolvedDealCardSpecV2;
  /** Admin context */
  isAdmin?: boolean;
  /** Whether panel is open */
  isOpen: boolean;
  /** Open panel handler */
  onOpen: () => void;
  /** Close panel handler */
  onClose: () => void;
  /** Panel configuration */
  config?: SidePanelPresentationConfig;
  /** Entry button configuration */
  entryConfig?: DetailsEntryConfig;
  /** The card content */
  children: React.ReactNode;
}

// Width class mapping
const WIDTH_CLASSES: Record<string, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
};

// ================================================================
// COMPONENT
// ================================================================

export function SidePanelPresentation({
  deal,
  spec,
  isAdmin = false,
  isOpen,
  onOpen,
  onClose,
  config = DEFAULT_SIDE_PANEL_CONFIG,
  entryConfig = DEFAULT_DETAILS_ENTRY,
  children,
}: SidePanelPresentationProps) {
  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpen();
  };
  
  const sheetSide = config.position === "left" ? "left" : "right";
  
  return (
    <>
      {/* Card with trigger */}
      <div className="relative">
        {children}
        
        {/* Entry button */}
        <button
          onClick={handleTriggerClick}
          className={cn(
            "absolute z-10 px-3 py-1.5 text-xs font-medium rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
            entryConfig.variant === "primary" && "bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-primary",
            entryConfig.variant === "secondary" && "bg-secondary text-secondary-foreground hover:bg-secondary/90 focus:ring-secondary",
            entryConfig.variant === "ghost" && "bg-background/80 text-foreground hover:bg-muted focus:ring-muted",
            entryConfig.position === "bottom-right" && "bottom-3 right-3",
            entryConfig.position === "bottom-left" && "bottom-3 left-3",
            entryConfig.position === "bottom-center" && "bottom-3 left-1/2 -translate-x-1/2",
          )}
          aria-label={`Show ${deal.id || "deal"} details`}
        >
          {entryConfig.label}
        </button>
      </div>
      
      {/* Side panel (Sheet) */}
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent 
          side={sheetSide}
          className={cn(
            "p-0 flex flex-col",
            WIDTH_CLASSES[config.width] || WIDTH_CLASSES.md,
          )}
        >
          {/* Header */}
          <SheetHeader className="flex-shrink-0 p-4 border-b bg-muted/30">
            <SheetTitle className="text-base font-semibold truncate pr-8">
              {getDealCardTitle(deal)}
            </SheetTitle>
            {getDealCardCondition(deal) && (
              <p className="text-sm text-muted-foreground truncate">
                {getDealCardCondition(deal)}
              </p>
            )}
          </SheetHeader>
          
          {/* Content - v2 blocks */}
          <div className="flex-1 overflow-y-auto p-4">
            <DetailsBlocksRenderer
              deal={deal}
              spec={spec}
              isAdmin={isAdmin}
              mode="panel"
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default SidePanelPresentation;
